import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  TablePagination,
  Tooltip,
  Collapse,
  InputAdornment,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import DevicesIcon from "@mui/icons-material/Devices";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "../../utils/axiosInstance";
import { formatReadableDate } from "../../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/**
 * DeviceAuditReport
 *
 * Props:
 *   onBack  – callback to return to the Audit view
 */
export default function DeviceAuditReport({ onBack }) {
  const [deviceId, setDeviceId] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedRows, setExpandedRows] = useState({});

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0]; // "YYYY-MM-DD"
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0]; // today
  });
  // ── Sort state ─────────────────────────────────────────────────────────
  // sortField: "username" | "time" | null
  // sortDir: "asc" | "desc"
  const [sortField, setSortField] = useState("time");
  const [sortDir, setSortDir] = useState("desc"); // username default A→Z
                    
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      // Default direction per column
      setSortDir(field === "username" ? "asc" : "desc");
    }
    setPage(0);
  };

  // ── Sort icon helper ─────────────────────────────────────────────────────
  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return <UnfoldMoreIcon fontSize="small" sx={{ color: "#555", ml: 0.5 }} />;
    return sortDir === "asc" ? (
      <ArrowUpwardIcon fontSize="small" sx={{ color: "#00BFFF", ml: 0.5 }} />
    ) : (
      <ArrowDownwardIcon fontSize="small" sx={{ color: "#00BFFF", ml: 0.5 }} />
    );
  };

  const toggleRow = (id) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleSearch = async () => {
    const id = inputValue.trim();
    if (!id) {
      setError("Please enter a Device ID.");
      return;
    }
    setError("");
    setDeviceId(id);
    setLoading(true);
    setSearched(false);
    setReport([]);
    setPage(0);

    try {
      const params = { search: id };

      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/logs/getAuditLogs`,
        { params, withCredentials: true }
      );
      const data = response?.data?.data || response?.data || [];
      setReport(data);
    } catch (err) {
      console.error("Error fetching device report:", err);
      setError("Failed to fetch report. Please check the Device ID and try again.");
      setReport([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleDownloadCSV = () => {
    const exportData = sortedReport.map((row) => ({
      Username: row.username || "---",
      Time: row.createdAt ? formatReadableDate(row.createdAt) : "---",
      Label: row.UI_Description || "---",
      "Action Description": row.action || "---",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `Device_Audit_${deviceId || "report"}.csv`);
  };

  // ── Sorted report ────────────────────────────────────────────────────────
  const sortedReport = useMemo(() => {
    let filtered = report;

    // Apply date range filter
    if (startDate || endDate) {
      filtered = report.filter((row) => {
        if (!row.createdAt) return false;
        const rowTime = new Date(row.createdAt).getTime();
        const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
        const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;
        if (start && rowTime < start) return false;
        if (end && rowTime > end) return false;
        return true;
      });
    }

    if (!sortField) return filtered;

    return [...filtered].sort((a, b) => {
      if (sortField === "username") {
        const valA = (a.username || "").toLowerCase();
        const valB = (b.username || "").toLowerCase();
        return sortDir === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      if (sortField === "time") {
        const valA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const valB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return sortDir === "asc" ? valA - valB : valB - valA;
      }
      return 0;
    });
  }, [report, sortField, sortDir, startDate, endDate]);

  const paginated = sortedReport.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ── Stat chips ────────────────────────────────────────────────────────────
  const uniqueUsers = [...new Set(report.map((r) => r.username).filter(Boolean))];
  const totalEntries = report.length;

  // ── Shared sortable header cell style ────────────────────────────────────
  const sortableCellSx = {
    fontWeight: "bold",
    textAlign: "center",
    fontSize: { xs: 12, md: 14 },
    bgcolor: "background.paper",
    cursor: "pointer",
    userSelect: "none",
    "&:hover": { bgcolor: "#1e2128" },
    transition: "background 0.15s",
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0.5, sm: 1.5 },
        height: "100%",
        position: "relative",
      }}
    >
      <Paper
        elevation={4}
        sx={{
          p: { xs: 1, sm: 2 },
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "background.paper",
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
            borderBottom: "2px solid",
            borderColor: "#00BFFF",
            pb: 1.5,
          }}
        >
          <Tooltip title="Back to Audit">
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                bgcolor: "#374151",
                color: "white",
                borderRadius: 0,
                "&:hover": { bgcolor: "#1565c0" },
                transition: "background 0.2s",
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <DevicesIcon sx={{ color: "#00BFFF", fontSize: 28 }} />

          <Typography
            variant="h5"
            fontWeight="bold"
            fontSize={{ xs: "1.1rem", sm: "1.5rem" }}
          >
            Device Audit Report
          </Typography>

          {searched && !loading && (
            <Chip
              icon={<AssessmentIcon fontSize="small" />}
              label={`${totalEntries} record${totalEntries !== 1 ? "s" : ""}`}
              size="small"
              sx={{ bgcolor: "#1565c0", color: "white", borderRadius: 0 }}
            />
          )}

          {/* ── CSV Export ── */}
          {searched && !loading && report.length > 0 && (
            <Box sx={{ ml: "auto" }}>
              <Button
                onClick={handleDownloadCSV}
                startIcon={<DownloadIcon fontSize="small" />}
                size="small"
                sx={{
                  bgcolor: "#1a3a1a",
                  color: "#4caf50",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 0,
                  border: "1px solid #4caf50",
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  px: 1.5,
                  py: 0.5,
                  "&:hover": { bgcolor: "#2e7d32", color: "white" },
                  transition: "all 0.2s",
                }}
              >
                Export CSV
              </Button>
            </Box>
          )}
        </Box>

        {/* ── Search bar ─────────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            mb: 2.5,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "55%", md: "45%" },
          }}
        >
          <TextField
            label="Device ID"
            variant="outlined"
            size="small"
            fullWidth
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={handleKeyDown}
            error={!!error}
            helperText={error}
            placeholder="e.g. DEVICE-2024-01-01-..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DevicesIcon fontSize="small" sx={{ color: "#00BFFF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 0 },
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            startIcon={loading ? null : <SearchIcon />}
            sx={{
              bgcolor: "#00BFFF",
              color: "#000",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: 0,
              height: 40,
              minWidth: 220,
              boxShadow: "none",
              "&:hover": { bgcolor: "#18a5d4" },
              "&.Mui-disabled": { bgcolor: "#374151", color: "#888" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#00BFFF" }} />
            ) : (
              "Generate Report"
            )}
          </Button>
        </Box>

        {/* ── Date Range Filter ── */}
        {searched && !loading && report.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              mb: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 }, minWidth: 170 }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(0); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || undefined }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 0 }, minWidth: 170 }}
            />
            {(startDate || endDate) && (
              <Button
                size="small"
                onClick={() => { setStartDate(""); setEndDate(""); setPage(0); }}
                sx={{
                  borderRadius: 0,
                  border: "1px solid #00BFFF",
                  color: "#00BFFF",
                  textTransform: "none",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  lineHeight:"1.9rem",
                  "&:hover": { bgcolor: "#1d2024", color: "white" },
                }}
              >
                Clear Dates
              </Button>
            )}
            {(startDate || endDate) && (
              <Chip
                size="small"
                label={`${sortedReport.length} of ${report.length} records`}
                sx={{ bgcolor: "#374151", color: "#00BFFF", borderRadius: 0 }}
              />
            )}
          </Box>
        )}

        {/* ── Stats row (shown after search) ─────────────────── */}
        {searched && !loading && report.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
              p: 1.5,
              bgcolor: "#1e2128",
              borderLeft: "3px solid #00BFFF",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <DevicesIcon sx={{ color: "#00BFFF", fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                Device:
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ color: "#00BFFF" }}
              >
                {deviceId}
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "#3a3c43" }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <PersonIcon sx={{ color: "#00BFFF", fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                Unique users:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {uniqueUsers.length}
              </Typography>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "#3a3c43" }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <AssessmentIcon sx={{ color: "#00BFFF", fontSize: 18 }} />
              <Typography variant="body2" color="text.secondary">
                Total actions:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {totalEntries}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Table ──────────────────────────────────────────── */}
        {loading ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: "#00BFFF" }} size={48} />
            <Typography color="text.secondary">
              Fetching device report…
            </Typography>
          </Box>
        ) : searched ? (
          <>
            <TableContainer
              sx={{ flexGrow: 1, height: { xs: "58vh", md: "60vh" } }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {/* Mobile expand */}
                    <TableCell
                      sx={{
                        display: { xs: "table-cell", md: "none" },
                        fontWeight: "bold",
                        textAlign: "center",
                        width: 48,
                        fontSize: 12,
                        bgcolor: "background.paper",
                      }}
                    >
                      +
                    </TableCell>

                    {/* ── Username (sortable) ── */}
                    <TableCell
                      onClick={() => handleSort("username")}
                      sx={sortableCellSx}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <PersonIcon fontSize="small" sx={{ color: "#00BFFF" }} />
                        Username
                        <SortIcon field="username" />
                      </Box>
                    </TableCell>

                    {/* ── Time (sortable) ── */}
                    <TableCell
                      onClick={() => handleSort("time")}
                      sx={sortableCellSx}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <AccessTimeIcon
                          fontSize="small"
                          sx={{ color: "#00BFFF" }}
                        />
                        Time
                        <SortIcon field="time" />
                      </Box>
                    </TableCell>

                    {/* UI Description – desktop only */}
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: 14,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <DescriptionIcon
                          fontSize="small"
                          sx={{ color: "#00BFFF" }}
                        />
                        Label
                      </Box>
                    </TableCell>

                    {/* action Description – desktop only */}
                    <TableCell
                      sx={{
                        display: { xs: "none", md: "table-cell" },
                        fontWeight: "bold",
                        textAlign: "center",
                        fontSize: 14,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <AssessmentIcon
                          fontSize="small"
                          sx={{ color: "#00BFFF" }}
                        />
                        action Description
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginated.length > 0 ? (
                    paginated.map((row, index) => (
                      <React.Fragment key={row.id || index}>
                        {/* ── Main Row ── */}
                        <TableRow
                          hover
                          sx={{
                            bgcolor:
                              index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            "&:hover": { bgcolor: "#4a4d54" },
                          }}
                        >
                          {/* Mobile expand toggle */}
                          <TableCell
                            sx={{
                              display: { xs: "table-cell", md: "none" },
                              textAlign: "center",
                              p: "4px",
                              width: 48,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(row.id || index)}
                              sx={{ color: "white", p: "4px" }}
                            >
                              {expandedRows[row.id || index] ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>

                          {/* Username */}
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontSize: { xs: 12, md: 14 },
                              fontWeight: 600,
                              color: "#00BFFF",
                            }}
                          >
                            {row.username || "---"}
                          </TableCell>

                          {/* Time */}
                          <TableCell
                            sx={{
                              textAlign: "center",
                              fontSize: { xs: 11, md: 13 },
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.createdAt
                              ? formatReadableDate(row.createdAt)
                              : "---"}
                          </TableCell>

                          {/* UI Description – desktop */}
                          <TableCell
                            sx={{
                              display: { xs: "none", md: "table-cell" },
                              textAlign: "center",
                              fontSize: 13,
                              maxWidth: 260,
                            }}
                          >
                            {row.UI_Description ? (
                              <Tooltip title={row.UI_Description} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 240,
                                    mx: "auto",
                                    cursor: "default",
                                    color: "text.primary",
                                  }}
                                >
                                  {row.UI_Description}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                ---
                              </Typography>
                            )}
                          </TableCell>

                          {/* action Description – desktop */}
                          <TableCell
                            sx={{
                              display: { xs: "none", md: "table-cell" },
                              textAlign: "center",
                              fontSize: 13,
                              maxWidth: 260,
                            }}
                          >
                            {row.action ? (
                              <Tooltip title={row.action || ""} arrow>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 240,
                                    mx: "auto",
                                    cursor: "default",
                                  }}
                                >
                                  {row.action}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                ---
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* ── Mobile Expanded Row ── */}
                        <TableRow
                          sx={{ display: { xs: "table-row", md: "none" } }}
                        >
                          <TableCell
                            colSpan={3}
                            style={{ paddingTop: 0, paddingBottom: 0 }}
                            sx={{
                              bgcolor:
                                index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            }}
                          >
                            <Collapse
                              in={expandedRows[row.id || index]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ py: 1.5, px: 1 }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.2,
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#00BFFF",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      UI Description:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "white", mt: 0.3 }}
                                    >
                                      {row.UI_Description || "---"}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#00BFFF",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      action Description:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "white", mt: 0.3 }}
                                    >
                                      {row.action || "---"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <DevicesIcon
                            sx={{ fontSize: 48, color: "#374151" }}
                          />
                          <Typography
                            color="text.secondary"
                            fontWeight={500}
                          >
                            No audit records found for device
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#00BFFF",
                              fontFamily: "monospace",
                              bgcolor: "#1e2128",
                              px: 2,
                              py: 0.5,
                            }}
                          >
                            {deviceId}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sortedReport.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        ) : (
          /* ── Idle state ── */
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              opacity: 0.55,
            }}
          >
            <DevicesIcon sx={{ fontSize: 72, color: "#374151" }} />
            <Typography
              variant="h6"
              color="text.secondary"
              fontWeight={500}
            >
              Enter a Device ID to generate its audit report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The report will show all actions performed on the device, who
              did them, and when.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}